import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { BelowCostApprovalDto, BelowCostDetails, BelowCostProposal, BelowCostRequestInput, InvoiceInput } from "@delta/shared";
import { useLang } from "../i18n";
import { api, ApiFailure, setBelowCostHandler, type ApiRequest } from "../lib/api";
import { errorText } from "../lib/errors";
import { amt } from "../pages/ledger/common";
import { Modal } from "./Modal";

// Sales below cost (migration 039; docs/correction-pass-2026-10-29.md, section 3). Every save that completes a sale
// (the ERP's Complete and Post, Edit invoice, the register's payment buttons) may be refused with
// below_cost_approval_*. The API layer then opens this dialog: the user asks for an approval, waits here until
// another user who may approve decides (Sales › Price controls), and continues with it. Nobody approves their own
// sale, approvers and the owner included (decisions of 29 Oct 2026; the server and migration 042 enforce it). Cancelling gives the refusal back to the screen, which shows it as before.
// POS pricing fix (30 Oct 2026): a request made here travels back with the refusal (`failure.body.approvalRequest`),
// so the register can restore the cart's prices and still keep the request, which closing this dialog never cancels.

/** The sale the refused request was about, as the approval request takes it. */
function saleOf(req: ApiRequest): BelowCostRequestInput | null {
  const body = (req.body ?? {}) as Record<string, unknown>;
  const now = new Date(Date.now() + 3 * 3600_000).toISOString().slice(0, 16);
  let m: RegExpMatchArray | null;
  if (req.method === "POST" && req.url === "/invoices") return { invoice: body as unknown as InvoiceInput };
  if (req.method === "PUT" && (m = req.url.match(/^\/invoices\/([^/]+)$/))) return { invoice: body as unknown as InvoiceInput, invoiceId: m[1] };
  if (req.method === "POST" && (m = req.url.match(/^\/invoices\/([^/]+)\/(complete|post)$/))) return { invoiceId: m[1] } as BelowCostRequestInput;
  if (req.method === "POST" && req.url === "/pos/sales") {
    return { invoiceId: (body.draftId as string) || null, invoice: { invoiceAt: now, warehouseId: body.warehouseId as string, partyId: (body.partyId as string) || null, reference: "", note: "",
      discountPct: (body.discountPct as string) ?? null, discountAmount: (body.discountAmount as string) ?? null, lines: body.lines as InvoiceInput["lines"], payments: [] } };
  }
  return null;
}

interface Ask { failure: ApiFailure; req: ApiRequest; resolve(id: string | null): void }

export function BelowCostProvider({ children }: { children: ReactNode }) {
  const [ask, setAsk] = useState<Ask | null>(null);
  useEffect(() => {
    setBelowCostHandler((failure, req) => new Promise((resolve) => setAsk({ failure, req, resolve })));
    return () => setBelowCostHandler(null);
  }, []);
  const done = useCallback((id: string | null, req?: BelowCostApprovalDto | null) => {
    if (ask && !id && req) ask.failure.body.approvalRequest = req;
    ask?.resolve(id); setAsk(null);
  }, [ask]);
  return <>{children}{ask && <BelowCostDialog key={ask.failure.message + ask.req.url} ask={ask} onDone={done} />}</>;
}

function BelowCostDialog({ ask, onDone }: { ask: Ask; onDone(id: string | null, req?: BelowCostApprovalDto | null): void }) {
  const { t } = useLang();
  const details = ask.failure.body.belowCost as BelowCostDetails | undefined;
  const sale = saleOf(ask.req);
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [req, setReq] = useState<BelowCostApprovalDto | null>(null);
  const poll = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearInterval(poll.current), []);
  const intro = ask.failure.code === "below_cost_approval_required" ? t("bcRequired") : errorText(t, ask.failure);

  const request = async () => {
    if (!sale) { setAlert(t("bcAskFailed")); return; }
    setBusy(true); setAlert(null);
    try {
      const a = await api<BelowCostApprovalDto>("POST", "/below-cost-approvals", sale);
      setReq(a);
      window.clearInterval(poll.current);
      poll.current = window.setInterval(async () => {
        try {
          const x = await api<BelowCostApprovalDto>("GET", `/below-cost-approvals/${a.id}`);
          setReq(x);
          if (x.status !== "pending") window.clearInterval(poll.current);
        } catch { /* keep waiting */ }
      }, 3000);
    } catch (e) { setAlert(errorText(t, e)); } finally { setBusy(false); }
  };

  const approved = req?.status === "approved";
  const submit = () => (approved ? onDone(req!.id) : req ? undefined : request());
  return (
    <Modal title={t("bcTitle")} wide onClose={() => onDone(null, req)} onSubmit={submit} alert={alert} busy={busy}
      saveLabel={approved ? t("bcContinue") : req?.status === "pending" ? t("pcStatus_pending") : t("bcAsk")}>
      <div className="bc-dlg" data-testid="below-cost">
        <p className="hint" data-testid="bc-intro">{intro}</p>
        {details && (
          <table className="tbl bc-lines">
            <thead><tr><th>{t("bcLine")}</th><th className="num">{t("qty")}</th><th className="num">{t("price")}</th><th className="num">{t("bcValue")}</th><th className="num">{t("bcCost")}</th></tr></thead>
            <tbody>{details.lines.map((l) => (
              <tr key={l.lineNo} data-testid="bc-line"><td>{(req?.lines.find((x) => x.lineNo === l.lineNo)?.item.code) || `#${l.lineNo}`}</td>
                <td className="num">{l.qty}</td><td className="num">{amt(l.price)}</td><td className="num">{amt(l.value)}</td>
                <td className="num" data-testid="bc-cost">{l.costUnknown ? <span className="hint" data-testid="bc-cost-unknown">{t("bcCostUnknown")}</span> : l.cost === null ? <span className="hint">{t("bcCostHidden")}</span> : amt(l.cost)}</td></tr>
            ))}</tbody>
          </table>
        )}
        {req?.proposal && <ProposalTable p={req.proposal} />}
        {req?.status === "pending" && <p className="pill warn" data-testid="bc-waiting">{t("bcWaiting")}</p>}
        {approved && <p className="pill good" data-testid="bc-approved">{t("bcApprovedBy", { u: req!.decidedByName ?? "", r: req!.reason ?? "" })}</p>}
        {req?.status === "rejected" && <p className="pill bad" data-testid="bc-rejected">{t("bcRejected", { u: req.decidedByName ?? "" })}</p>}
      </div>
    </Modal>
  );
}

/** The sale an approval request proposes: each line's list price next to the proposed price, the discounts, fees and total. */
export function ProposalTable({ p }: { p: BelowCostProposal }) {
  const { t, L } = useLang();
  const fees = p.lines.some((l) => /[1-9]/.test(l.fee));
  return (
    <table className="tbl bc-lines bc-proposal" data-testid="bc-proposal">
      <thead><tr><th>{t("itemName")}</th><th className="num">{t("qty")}</th><th className="num">{t("bcListPrice")}</th><th className="num">{t("bcProposedPrice")}</th><th className="num">{t("discount")}</th>
        {fees && <th className="num">{t("bcFee")}</th>}<th className="num">{t("total")}</th></tr></thead>
      <tbody>{p.lines.map((l) => {
        const changed = l.listPrice === null || Number(l.listPrice) !== Number(l.price);
        return (
          <tr key={l.lineNo} data-testid="bc-proposal-line" className={changed ? "changed" : undefined}>
            <td>{l.item.code} {L(l.item)}</td><td className="num">{l.qty} {L(l.unit)}</td>
            <td className="num" data-testid="bc-list-price">{l.listPrice === null ? "–" : amt(l.listPrice)}</td>
            <td className="num" data-testid="bc-proposed-price">{changed ? <b>{amt(l.price)}</b> : amt(l.price)}</td>
            <td className="num">{l.discountPct ? `${l.discountPct}%` : l.discountAmount ? amt(l.discountAmount) : "–"}</td>
            {fees && <td className="num">{amt(l.fee)}</td>}<td className="num">{amt(l.total)}</td>
          </tr>
        );
      })}</tbody>
      <tfoot><tr><td colSpan={fees ? 6 : 5}>{t("bcProposedTotal")}{p.discountPct ? ` · ${t("discount")} ${p.discountPct}%` : ""}</td><td className="num" data-testid="bc-proposed-grand"><b>{amt(p.totals.grand)}</b></td></tr></tfoot>
    </table>
  );
}
